import { ChangeEvent, KeyboardEvent, useCallback, useContext, useState } from "react"
import { ClassProps } from "../../utility/common/props"
import cn from "classnames"
import { faSearch, faTimes } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import LoadingButton from "../LoadingButton/LoadingButton"
import { Button, FormControl, FormCheck } from "react-bootstrap"
import { formTranslation } from "../../locales/form"
import styles from "./SearchArticlesInput.module.scss"
import debounce from "lodash/debounce"
import { useTranslation } from "react-i18next"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import { MIN_QUERY_LENGTH_TO_SEARCH } from "."
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"

const tNamespace = "knowledgeBase:search-articles."

export type CommonSearchArticlesInputProps = Pick<
    SearchArticlesInputProps,
    | "searchBtnClassName"
    | "inputClassName"
    | "clearBtnClassName"
    | "disableRemoved"
    | "className"
    | "searchButtonTestId"
    | "searchInputTestId"
    | "clearButtonTestId"
>

export interface SearchArticlesInputProps extends ClassProps {
    query: string
    enableRemoved: boolean
    loading: boolean
    onSearch: () => void
    onClear: () => void
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
    onEnableRemoved: () => void
    isFiltersEnabled: boolean
    disableRemoved?: boolean
    searchBtnClassName?: string
    inputClassName?: string
    clearBtnClassName?: string
    searchButtonTestId?: string
    searchInputTestId?: string
    clearButtonTestId?: string
}

export const SearchArticlesInput = (props: SearchArticlesInputProps) => {
    const {
        query,
        loading,
        onSearch,
        onClear,
        onChange,
        onKeyDown,
        className,
        enableRemoved,
        onEnableRemoved,
        disableRemoved,
        isFiltersEnabled,
        searchBtnClassName,
        inputClassName,
        clearBtnClassName,
        searchButtonTestId,
        searchInputTestId,
        clearButtonTestId
    } = props
    const [searchValue, setSearchValue] = useState<string>(query)
    const { t } = useTranslation()
    const { shareId, projectId } = useContext(KnowledgeBaseContext)
    const isShared = Boolean(shareId)
    const handleChangeCallback = useCallback(
        debounce((e: ChangeEvent<HTMLInputElement>) => {
            onChange(e)
        }, 800),
        []
    )

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleChangeCallback(e)
        setSearchValue(e.target.value)
    }

    const handleClear = () => {
        setSearchValue("")
        onClear()
    }

    const isQueryValid = query && query.length >= MIN_QUERY_LENGTH_TO_SEARCH

    return (
        <div>
            <div className={cn(styles.search, className)}>
                <LoadingButton
                    className={cn(styles.search__searchBtn, searchBtnClassName)}
                    loading={loading}
                    variant="link"
                    disabled={!query}
                    onClick={onSearch}
                    testId={searchButtonTestId ?? testId.searchArticlesButton}
                >
                    <FontAwesomeIcon icon={faSearch} size="1x" />
                </LoadingButton>

                <FormControl
                    type="text"
                    name="search-articles"
                    value={searchValue}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    placeholder={t(formTranslation.search)}
                    className={cn(styles.search__input, inputClassName)}
                    data-testid={searchInputTestId ?? testId.searchArticlesInput}
                />

                {(query || isFiltersEnabled) && !loading && (
                    <Button
                        className={cn(styles.search__clearBtn, clearBtnClassName)}
                        variant="link"
                        onClick={handleClear}
                        data-testid={clearButtonTestId ?? testId.clearSearchArticlesInput}
                    >
                        <div className={styles.search__clearIcon}>
                            <FontAwesomeIcon icon={faTimes} size="1x" />
                        </div>
                    </Button>
                )}
            </div>
            {isQueryValid && !disableRemoved && !isShared && (
                <FormCheck
                    custom
                    id="enable-removed"
                    label={t(`${tNamespace}enable-removed`)}
                    checked={enableRemoved}
                    onChange={onEnableRemoved}
                    className={styles.enableRemoved}
                />
            )}
        </div>
    )
}
